import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Lightning, Logo } from '../components/Icons.jsx';

const passages = [
  {
    title: 'The placebo effect',
    text: `In medicine, the placebo effect describes a measurable improvement in a patient's condition that cannot be explained by the active ingredient of a treatment. The effect is most pronounced for symptoms that depend on subjective reporting, such as pain or fatigue, but it has also been observed in conditions with objective markers. Researchers now understand that expectation, conditioning and the broader clinical encounter all contribute. The implication for trial design is significant: any treatment must be shown to outperform a credible placebo before clinical claims can be supported. Some bioethicists argue that, where no proven treatment exists, the controlled use of placebo can be both effective and ethical.`
  },
  {
    title: 'NHS waiting times',
    text: `Waiting times in the NHS have been the focus of policy debate for decades. Although total funding has increased in real terms, the demand created by an ageing population and the long tail of pandemic-deferred care has outpaced capacity gains. Workforce shortages, particularly among trained nurses and general practitioners, are the most cited cause by trust-level leaders. Reforms targeting elective backlogs have included independent-sector partnerships and centralised triage hubs, but the impact on emergency department waits has been smaller. Patient advocacy groups argue that any sustainable improvement requires not only more clinicians but also primary care reform that prevents avoidable acute presentations.`
  },
  {
    title: 'Antibiotic resistance',
    text: `Antibiotic resistance is one of the most pressing global health threats of the twenty-first century. Bacteria evolve rapidly under selection pressure, and the overuse of broad-spectrum antibiotics in both human medicine and agriculture has accelerated the emergence of resistant strains. Once-routine infections — urinary tract, post-surgical, respiratory — are returning to the threshold of untreatable. Antimicrobial stewardship programmes encourage clinicians to narrow prescriptions, shorten courses and confirm bacterial cause where possible. Public-facing campaigns, by contrast, focus on patient expectation: a sore throat does not need an antibiotic, but the doctor needs the patient to accept that.`
  },
  {
    title: 'Informed consent',
    text: `Informed consent is a cornerstone of medical ethics and law. For consent to be valid, the patient must have capacity, be given sufficient information, and make the decision voluntarily. The landmark Montgomery v Lanarkshire ruling in 2015 shifted UK consent law from a doctor-centred standard — what the reasonable doctor would disclose — to a patient-centred one: what the reasonable patient in this individual's position would want to know. This places a higher duty on clinicians to discuss not only common risks but also any risk that a particular patient would regard as significant. The standard applies equally to dental procedures, surgical interventions and non-emergency treatments.`
  }
];

const paces = [
  { label: 'Slow', ms: 550, wpm: '~110 WPM' },
  { label: 'Medium', ms: 330, wpm: '~180 WPM' },
  { label: 'Fast', ms: 220, wpm: '~270 WPM' },
  { label: 'Sprint', ms: 150, wpm: '~400 WPM' },
];

export default function DrillSubVocal() {
  const nav = useNavigate();
  const [stage, setStage] = useState('brief');
  const [paceIdx, setPaceIdx] = useState(1);
  const [passageIdx, setPassageIdx] = useState(0);
  const [wordIdx, setWordIdx] = useState(0);
  const [countdown, setCountdown] = useState(3);
  const startRef = useRef(0);
  const [elapsed, setElapsed] = useState(0);

  const pace = paces[paceIdx];
  const passage = passages[passageIdx];
  const words = useMemo(() => passage.text.split(/\s+/).filter(Boolean), [passage.text]);

  useEffect(() => {
    if (stage !== 'countdown') return;
    if (countdown <= 0) {
      startRef.current = Date.now();
      setStage('running');
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 800);
    return () => clearTimeout(t);
  }, [stage, countdown]);

  useEffect(() => {
    if (stage !== 'running') return;
    if (wordIdx >= words.length) {
      setElapsed(Date.now() - startRef.current);
      setStage('done');
      return;
    }
    const t = setTimeout(() => setWordIdx((i) => i + 1), pace.ms);
    return () => clearTimeout(t);
  }, [stage, wordIdx, words.length, pace.ms]);

  const wpm = elapsed ? Math.round((words.length / elapsed) * 60000) : 0;

  function startDrill() {
    setWordIdx(0);
    setCountdown(3);
    setElapsed(0);
    setStage('countdown');
  }

  function exit() {
    if (stage === 'running' && !confirm('Exit this drill? Your progress will be lost.')) return;
    nav('/reading-lab');
  }

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <header style={{ background: '#fff', borderBottom: '1px solid var(--border)', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={exit} className="back-link" style={{ marginBottom: 0 }}>
          <ArrowLeft width="16" height="16" />Exit drill
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo width="28" height="28" onLight />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1, fontFamily: 'Montserrat, sans-serif' }}>
            <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--brand-deep)' }}>Medi</span>
            <span style={{ fontSize: 8.5, fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: 3 }}>powered by Medic Mind</span>
          </div>
        </div>
        <div style={{ minWidth: 80, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)', fontSize: 14 }}>
          {stage === 'running' && `${wordIdx} / ${words.length}`}
        </div>
      </header>

      <main className="main drill-shell">
        {stage === 'brief' && (
          <>
            <div className="page-head">
              <div className="kicker" style={{ color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12 }}>
                <Lightning width="14" height="14" style={{ verticalAlign: 'middle', marginRight: 6 }} />Speed Reader · Drill 02
              </div>
              <h1>Eliminate sub-vocalisation</h1>
              <p className="subtitle">Words are highlighted one at a time through the full passage. Your job: stop the inner voice. Don't say words mentally — let your eyes sweep forward and absorb meaning directly.</p>
            </div>

            <div className="card" style={{ marginBottom: 18 }}>
              <h3 style={{ margin: '0 0 14px', fontSize: 18 }}>Settings</h3>
              <Chips label="Highlight pace" hint={pace.wpm}
                options={paces.map((p) => p.label)} value={pace.label}
                onChange={(v) => setPaceIdx(paces.findIndex((p) => p.label === v))} />
              <Chips label="Passage" hint={`${words.length} words`}
                options={passages.map((p) => p.title)} value={passage.title}
                onChange={(v) => setPassageIdx(passages.findIndex((p) => p.title === v))} />
            </div>

            <button onClick={startDrill} className="btn btn-primary" style={{ fontSize: 16, padding: '14px 24px' }}>
              Start drill <ArrowRight width="18" height="18" />
            </button>
          </>
        )}

        {stage === 'countdown' && (
          <div className="drill-stage" style={{ marginTop: 60 }}>
            <div style={{ fontSize: 96, fontWeight: 700, color: 'var(--brand-purple)', letterSpacing: '-0.02em' }}>
              {countdown || 'Read!'}
            </div>
            <p style={{ color: 'var(--text-muted)', marginTop: 14 }}>No inner voice. Eyes only.</p>
          </div>
        )}

        {stage === 'running' && (
          <div style={{ maxWidth: 700, margin: '24px auto', padding: '0 20px' }}>
            <div style={{ height: 6, background: 'var(--card-soft)', borderRadius: 99, marginBottom: 28 }}>
              <div style={{
                width: `${(wordIdx / words.length) * 100}%`, height: '100%',
                background: 'linear-gradient(90deg, var(--brand-purple), var(--brand-teal))',
                borderRadius: 99, transition: `width ${pace.ms * 0.9}ms linear`
              }} />
            </div>
            <p style={{ fontSize: 17, lineHeight: 2.1, color: 'var(--text)', userSelect: 'none' }}>
              {words.map((w, i) => (
                <span key={i} style={{
                  background: i === wordIdx ? '#fde68a' : 'transparent',
                  color: i < wordIdx ? 'var(--text-faint)' : 'var(--text)',
                  borderRadius: 3, padding: '1px 2px',
                }}>{w}{' '}</span>
              ))}
            </p>
            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <button onClick={() => { setElapsed(Date.now() - startRef.current); setStage('done'); }} className="btn btn-secondary">
                End early
              </button>
            </div>
          </div>
        )}

        {stage === 'done' && (
          <>
            <div className="page-head">
              <div className="kicker" style={{ color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12 }}>Drill complete</div>
              <h1>Nice work.</h1>
              <p className="subtitle">You read "{passage.title}" at {pace.label.toLowerCase()} pace.</p>
            </div>

            <div className="drill-summary">
              <div className="stat"><div className="v">{wpm}</div><div className="l">Words per minute</div></div>
              <div className="stat"><div className="v">{pace.label}</div><div className="l">Pace</div></div>
              <div className="stat"><div className="v">{(elapsed / 1000).toFixed(1)}s</div><div className="l">Total time</div></div>
            </div>

            <div className="card" style={{ marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>Key habit</h3>
              <p style={{ color: 'var(--text-2)', margin: 0 }}>
                Sub-vocalisation adds ~50ms per word. At a 250-word UCAT passage, that's 12 extra seconds you don't have. The highlight is a pacemaker — let meaning arrive without the phonological loop firing. Ten sessions at Medium pace is usually enough to break the habit. Then push to Fast.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={startDrill} className="btn btn-primary">Run another <ArrowRight width="16" height="16" /></button>
              <Link to="/reading-lab" className="btn btn-secondary">Back to Reading Lab</Link>
              <Link to="/drill/memory-trainer" className="btn btn-secondary">Try Memory Trainer →</Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Chips({ label, hint, options, value, onChange }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <strong style={{ fontSize: 14 }}>{label}</strong>
        {hint && <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{hint}</span>}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {options.map((opt) => (
          <button key={opt} onClick={() => onChange(opt)} style={{
            padding: '8px 14px', borderRadius: 999, fontSize: 14,
            border: `1px solid ${opt === value ? 'var(--brand-purple)' : 'var(--border)'}`,
            background: opt === value ? 'var(--accent-soft)' : '#fff',
            color: opt === value ? 'var(--brand-deep)' : 'var(--text-2)',
            fontWeight: opt === value ? 600 : 500,
          }}>{opt}</button>
        ))}
      </div>
    </div>
  );
}
